import { Button, Checkbox } from '@restomarker/ui-kit';
import { useState, useSyncExternalStore, type ReactElement } from 'react';

import { t } from '@/shared/i18n';
import { systemNotificationSupport } from '@/shared/lib/systemNotification';

import styles from '../notificationsDialog.module.css';
import { showTestNotification, turnOnDeviceNotifications } from './deviceNotifications';
import {
  deviceMarksVersion,
  notifyEnabled,
  setNotifyEnabled,
  subscribeDeviceMarks,
} from './model/deviceMarks';

export interface DeviceNotificationsSectionProps {
  /** Саппорт-сессия: устройство платформы, галочку не трогаем. */
  disabled: boolean;
}

/**
 * «Уведомления Windows» — галочка САМОГО устройства (T-047, решение владельца 3).
 *
 * Поставить её может любой на смене, и она переживает пересменку: ключ со временем
 * жизни `device`. Нет Windows-оболочки — секции нет вовсе.
 *
 * «Проверить уведомление» (№10, 2026-09-30) показывает пробное сразу, без сервера:
 * так видно, пропускает ли их сам Windows, отдельно от того, пришло ли что-то в ленту.
 */
export function DeviceNotificationsSection({
  disabled,
}: DeviceNotificationsSectionProps): ReactElement | null {
  useSyncExternalStore(subscribeDeviceMarks, deviceMarksVersion, deviceMarksVersion);
  const support = systemNotificationSupport();
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tested, setTested] = useState(false);
  if (support === 'none') return null;

  async function toggle(next: boolean): Promise<void> {
    setBusy(true);
    try {
      if (next) {
        // Windows не разрешила — ключ не пишется: галочка, за которой Windows ничего
        // не покажет, обещала бы вхолостую.
        setDenied(!(await turnOnDeviceNotifications()));
        return;
      }
      setDenied(false);
      await setNotifyEnabled(false);
    } finally {
      setBusy(false);
    }
  }

  async function test(): Promise<void> {
    setBusy(true);
    try {
      const shown = await showTestNotification();
      setDenied(!shown);
      setTested(shown);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.body} aria-label={t('alerts.device.section')}>
      <h3 className={styles.sectionTitle}>{t('alerts.device.section')}</h3>
      <Checkbox
        checked={support === 'ok' && notifyEnabled()}
        disabled={disabled || busy || support !== 'ok'}
        onCheckedChange={(next) => void toggle(next)}
        label={t('alerts.device.toggle')}
        hint={t('alerts.device.hint')}
      />
      {support === 'ok' ? (
        <div>
          <Button variant="secondary" disabled={disabled || busy} onClick={() => void test()}>
            {t('alerts.device.test')}
          </Button>
        </div>
      ) : null}
      {tested ? (
        <p className={styles.status} role="status">
          {t('alerts.device.tested')}
        </p>
      ) : null}
      {support === 'needsShellUpdate' ? (
        <p className={styles.muted}>{t('alerts.device.needsUpdate')}</p>
      ) : (
        <p className={styles.muted}>{t('alerts.device.help')}</p>
      )}
      {denied ? (
        <p className={styles.problem} role="alert">
          {t('alerts.device.denied')}
        </p>
      ) : null}
    </section>
  );
}
